import { useState } from 'react';
import { useAction, useQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api';

const TIERS = [
  { key: 'solo' as const, name: 'Solo', price: '$49 / month', blurb: 'One technician.' },
  { key: 'team' as const, name: 'Team', price: '$99 / month', blurb: 'Up to five technicians.' },
  {
    key: 'founding' as const,
    name: 'Founding operator',
    price: '$399 first year',
    blurb: 'First ten operators only. Normally $588.',
  },
];

export default function Billing() {
  const billing = useQuery(api.billing.current, {});
  const checkout = useAction(api.billing.createCheckoutSession);
  const portal = useAction(api.billing.createPortalSession);
  const [busy, setBusy] = useState<string | null>(null);

  async function start(plan: 'solo' | 'team' | 'founding') {
    setBusy(plan);
    try {
      const { url } = await checkout({
        plan,
        successUrl: `${window.location.origin}/settings?checkout=done`,
        cancelUrl: window.location.href,
      });
      window.location.href = url;
    } finally {
      setBusy(null);
    }
  }

  if (billing === undefined) return <p className="muted">Loading…</p>;

  return (
    <>
      <h1>Billing</h1>

      <div className="card">
        <div className="spread">
          <div>
            <strong>{billing.label}</strong>
            <p className="muted small">
              {billing.active
                ? `Active${billing.currentPeriodEnd ? ` until ${new Date(billing.currentPeriodEnd).toLocaleDateString('en-AU')}` : ''}`
                : billing.status === 'past_due'
                  ? 'Payment failed — update your card to keep your team signed in.'
                  : 'No active subscription.'}
              {billing.source ? ` · billed through ${billing.source === 'stripe' ? 'card' : 'the app store'}` : ''}
            </p>
          </div>
          {billing.source === 'stripe' ? (
            <button
              className="quiet"
              onClick={async () => {
                const { url } = await portal({ returnUrl: window.location.href });
                window.location.href = url;
              }}
            >
              Manage card &amp; plan
            </button>
          ) : null}
        </div>

        {billing.seatsExceeded ? (
          <p className="small" style={{ color: 'var(--alert)' }}>
            {billing.seatsUsed} technicians active on a {billing.seats}-seat plan.
          </p>
        ) : null}
      </div>

      {!billing.active ? (
        <div className="tiles">
          {TIERS.map((tier) => (
            <div className="tile" key={tier.key}>
              <strong>{tier.name}</strong>
              <span className="n" style={{ fontSize: '1.2rem' }}>{tier.price}</span>
              <span className="l">{tier.blurb}</span>
              <button
                style={{ marginTop: '.75rem' }}
                disabled={busy !== null}
                onClick={() => void start(tier.key)}
              >
                {busy === tier.key ? 'Opening…' : 'Choose'}
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <div className="card">
        <p className="muted small">
          Your asset register, test history and CSV exports stay available whatever happens to
          your subscription, and your clients keep their portal. Your records are yours.
        </p>
      </div>
    </>
  );
}
